import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dropdown } from '@carbon/react';
import { SettingsNavItem, SettingsNavSection } from 'src/config/settingsNav';

interface SettingsNavDropdownProps {
  sections: SettingsNavSection[];
}

const SettingsNavDropdown = React.memo(({ sections }: SettingsNavDropdownProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const items = useMemo(
    () =>
      sections.flatMap((section) =>
        section.items.map((item) => ({
          ...item,
          // Always qualify with the section, matching the sidebar. Collapsing a
          // single-item section to its section name alone hid the item's own label.
          label: `${t(section.labelKey)} › ${t(item.labelKey)}`,
        }))
      ),
    [sections, t]
  );

  const selected = items.find((item) => item.path === pathname) || items[0];

  return (
    <Dropdown
      id={'settings-nav-dropdown'}
      className={'settings-nav-dropdown'}
      titleText={t('settingsNav.title')}
      hideLabel
      label={t('settingsNav.title')}
      items={items}
      selectedItem={selected}
      itemToString={(item) => (item as { label: string })?.label || ''}
      onChange={({ selectedItem }) => {
        const next = selectedItem as SettingsNavItem | null;
        if (next) {
          navigate(next.path);
        }
      }}
    />
  );
});

export default SettingsNavDropdown;
